/**
 * The chat bubble's markup, and what the After Hours launcher adds to it (MAR-391).
 *
 * One source for two readers: the live widget (`MarvaChat.createChatBubble` writes the bubble;
 * `buildAfterHoursTakeover` reskins it into the launcher with the pieces below) and the merchant
 * app's chat preview (`src/preview/chat-preview.js`), which draws the reskinned launcher as a
 * static string. `chat-launcher.test.js` pins the live widget's DOM byte-for-byte and proves the
 * preview's string parses to the same DOM.
 *
 * Pure strings and functions only — no DOM, no `window` — so the preview can import it outside
 * a browser.
 *
 * @module @marva/embed-script/src/sdk/chat-launcher
 */

'use strict';

const { escapeHtml } = require('./html-safety');

const BUBBLE_PLACEHOLDER = 'Ask me anything...';

/** The orb the After Hours launcher puts first inside the bubble. */
const LAUNCHER_ORB_HTML = '<div class="w-orb launcher-orb"></div>';

/** The classes, after the theme class, that turn the bubble's container into the launcher. */
const LAUNCHER_CLASSES = Object.freeze(['marva-afterhours', 'marva-launcher']);

/** The launcher's persona-aware placeholder (an attribute value, unescaped). */
function launcherPlaceholder(assistantName) {
  return `Ask ${assistantName}…`;
}

/**
 * The bubble's inner markup — the container's `innerHTML`. With no options this is exactly what
 * the live widget writes; `{ orb, placeholder }` gives the launcher's reskinned bubble, the orb
 * landing where `insertAdjacentHTML('afterbegin', …)` puts it.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.orb=false]
 * @param {string} [opts.placeholder] - unescaped; escaped here.
 * @returns {string}
 */
function chatBubbleHtml({ orb = false, placeholder = BUBBLE_PLACEHOLDER } = {}) {
  return `
                <div class="marva-chat-bubble">${orb ? LAUNCHER_ORB_HTML : ''}
                    <div class="marva-drag-handle">⋮⋮</div>
                    <input type="text" class="marva-bubble-input" placeholder="${escapeHtml(placeholder)}" />
                    <button class="marva-compare-btn" style="display: none;" title="Compare liked products">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 6h18M3 12h18M3 18h18"/>
                        <circle cx="8" cy="6" r="2" fill="currentColor"/>
                        <circle cx="16" cy="12" r="2" fill="currentColor"/>
                        <circle cx="10" cy="18" r="2" fill="currentColor"/>
                      </svg>
                    </button>
                    <button class="marva-bubble-send">↑</button>
                </div>
            `;
}

/** The whole After Hours launcher as the live widget leaves it once reskinned. */
function afterHoursLauncherHtml({ themeClass, assistantName }) {
  return `<div class="marva-chat ${themeClass} ${LAUNCHER_CLASSES.join(' ')}">` +
    chatBubbleHtml({ orb: true, placeholder: launcherPlaceholder(assistantName) }) +
    '</div>';
}

module.exports = {
  BUBBLE_PLACEHOLDER,
  LAUNCHER_CLASSES,
  LAUNCHER_ORB_HTML,
  afterHoursLauncherHtml,
  chatBubbleHtml,
  launcherPlaceholder
};
